/* eslint-disable check-file/filename-blocklist */
import { PRIMARY_COLORS, SECONDARY_COLORS, HOVER_COLORS } from '../colors.ts';
import { hexToToken } from '../helpers.ts';

import type { ContentState } from './types.ts';

const MAX_ALLOWED_CUSTOM_STATES = 3;

export function determineSecondaryColor(
	primaryColor?: string | null,
):
	| 'var(--ds-background-accent-red-subtlest)'
	| 'var(--ds-background-accent-yellow-subtlest)'
	| 'var(--ds-background-accent-green-subtlest)'
	| 'var(--ds-background-accent-blue-subtlest)'
	| 'var(--ds-background-accent-purple-subtlest)' {
	const tokenPrimaryColor = hexToToken(primaryColor ?? PRIMARY_COLORS.BLUE);
	switch (tokenPrimaryColor) {
		case PRIMARY_COLORS.BLUE:
			return SECONDARY_COLORS.BLUE;
		case PRIMARY_COLORS.GREEN:
			return SECONDARY_COLORS.GREEN;
		case PRIMARY_COLORS.YELLOW:
			return SECONDARY_COLORS.YELLOW;
		case PRIMARY_COLORS.RED:
			return SECONDARY_COLORS.RED;
		case PRIMARY_COLORS.PURPLE:
			return SECONDARY_COLORS.PURPLE;
		default:
			return SECONDARY_COLORS.BLUE;
	}
}

export function determineHoverColor(
	secondaryColor: string | null,
):
	| 'var(--ds-background-accent-red-subtlest-hovered)'
	| 'var(--ds-background-accent-yellow-subtlest-hovered)'
	| 'var(--ds-background-accent-green-subtlest-hovered)'
	| 'var(--ds-background-accent-blue-subtlest-hovered)'
	| 'var(--ds-background-accent-purple-subtlest-hovered)' {
	const tokenSecondaryColor = hexToToken(secondaryColor ?? SECONDARY_COLORS.BLUE);
	switch (tokenSecondaryColor) {
		case SECONDARY_COLORS.BLUE:
			return HOVER_COLORS.BLUE;
		case SECONDARY_COLORS.GREEN:
			return HOVER_COLORS.GREEN;
		case SECONDARY_COLORS.YELLOW:
			return HOVER_COLORS.YELLOW;
		case SECONDARY_COLORS.RED:
			return HOVER_COLORS.RED;
		case SECONDARY_COLORS.PURPLE:
			return HOVER_COLORS.PURPLE;
		default:
			return HOVER_COLORS.BLUE;
	}
}

export function isDuplicate(
	oldContentState: ContentState | null,
	newContentState: ContentState,
): boolean {
	if (!oldContentState) return false; // can't be duplicate if we don't even have a content state originally
	return (
		oldContentState.name === newContentState.name && oldContentState.color === newContentState.color
	);
}

export function findDuplicateStateIdAndIndex(
	customStatesArray: (ContentState | null)[],
	newCustomState: ContentState,
): {
	index: number;
	id: string | null | undefined;
} | null {
	for (let i = 0; i < customStatesArray.length; ++i) {
		if (isDuplicate(customStatesArray[i], newCustomState)) {
			return { index: i, id: customStatesArray[i]?.id };
		}
	}
	// if we don't find a match just return null
	return null;
}

export function createNewCustomStatesArrayWhenNewCustomAdded(
	oldCustomStatesArray: (ContentState | null)[],
	newCustomState: ContentState,
): (ContentState | null)[] {
	/* Need to check if the state user entered matches an existing custom one in the array.
  If it's a duplicate, it means the state the user just typed in is one that already exists. We must find the index
  and id in the old array and handle it like if an existing custom state was selected
   */
	const idAndIndex = findDuplicateStateIdAndIndex(oldCustomStatesArray, newCustomState);
	if (idAndIndex) {
		// user typed in an existing custom state
		return createNewCustomStatesArrayWhenExistingCustomSelected(
			oldCustomStatesArray,
			{ ...newCustomState, id: idAndIndex.id },
			idAndIndex.index,
		);
	} else {
		const newArray = [newCustomState, ...oldCustomStatesArray];
		if (newArray.length > MAX_ALLOWED_CUSTOM_STATES) {
			newArray.pop();
		}
		return newArray;
	}
}

export function createNewCustomStatesArrayWhenExistingCustomSelected(
	oldCustomStatesArray: (ContentState | null)[],
	selectedCustomState: ContentState,
	index: number,
): (ContentState | null)[] {
	// removed the selected custom state by index and then add it to the top of the new array
	const newArray = [...oldCustomStatesArray];
	newArray.splice(index, 1);
	newArray.unshift(selectedCustomState);
	return newArray;
}

export const OPTIMISTIC_CUSTOM_STATE_ID_PREFIX = 'temp-';

export function reconcileCustomStatesAfterCreate(
	oldCustomStatesArray: (ContentState | null)[],
	newCustomState: ContentState,
): ContentState[] {
	// Drop null entries, the optimistic placeholder, and any prior entry that matches the
	// newly-created state by name or id, so the server state is added exactly once. The type
	// guard narrows the result to ContentState[] (no nulls survive the filter).
	const withoutPlaceholders = oldCustomStatesArray.filter(
		(state): state is ContentState =>
			state != null &&
			!state.id?.startsWith(OPTIMISTIC_CUSTOM_STATE_ID_PREFIX) &&
			state.name !== newCustomState.name &&
			(newCustomState.id == null || state.id !== newCustomState.id),
	);

	const newArray = [newCustomState, ...withoutPlaceholders];
	if (newArray.length > MAX_ALLOWED_CUSTOM_STATES) {
		newArray.length = MAX_ALLOWED_CUSTOM_STATES;
	}
	return newArray;
}

// Re-add the applied custom status to the refetched option list only when it is still locally
// known (a just-created status a stale refetch hasn't caught up to). A status disabled (deleted)
// in view mode is gone from the local list, so it is not resurfaced. Returns the option list to use.
export function reAddAppliedCustomStatusIfMissing(
	appliedContentState: ContentState | null,
	refetchedCustomStatesArray: (ContentState | null)[],
	defaultStatesArray: (ContentState | null)[],
	locallyKnownCustomStatesArray: (ContentState | null)[],
): (ContentState | null)[] {
	const isAppliedCustom =
		!!appliedContentState?.id && !defaultStatesArray.some((s) => s?.id === appliedContentState.id);
	const missingFromRefetch =
		isAppliedCustom && !refetchedCustomStatesArray.some((s) => s?.id === appliedContentState?.id);
	const knownLocally = locallyKnownCustomStatesArray.some((s) => s?.id === appliedContentState?.id);
	if (missingFromRefetch && knownLocally && appliedContentState) {
		return [appliedContentState, ...refetchedCustomStatesArray];
	}
	return refetchedCustomStatesArray;
}
